/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { SerializedError } from '@reduxjs/toolkit';
import messages from 'services/locales/en/messages';
import { Artist, SongType } from '../entities';
import {
  SongMetadataResponse,
  ReleaseListResponse,
  ReleaseResponse,
  ErrorResponse,
  ExceptionCodeErrorData,
  ReleaseValidationErrorData,
  ReleaseSongResponse,
  ReleasesStatsResponse,
} from '../responses/responseTypes';
import { getSongStatus } from './helpers/getReleaseSongStatus';

export const transformToSong = (
  song: ReleaseSongResponse,
  songType: SongType
) => ({
  songType,
  id: song.id,
  title: song.title,
  duration: song.duration,
  isInLibrary: song.is_in_library,
  musicFilePath: song.music_file_path,
  wavFilePath: song.wav_file_path,
  status: getSongStatus(song, songType),
});

const transformSongMetadata = ({
  has_vocals,
  vocals_content,
  additional_artists,
  ...data
}: SongMetadataResponse) => ({
  ...data,
  vocalsContent: vocals_content,
  hasVocals: has_vocals,
  additionalArtists: additional_artists,
});

export const transformToUserSong = (song: ReleaseSongResponse) => ({
  ...transformToSong(song, SongType.USER_UPLOADED),
  metadata: song.metadata ? transformSongMetadata(song.metadata) : null,
});

export const transformToRelease = ({
  artist_name,
  cover_art,
  ...data
}: ReleaseResponse) => ({
  ...data,
  artistName: artist_name,
  coverArtPath: cover_art?.thumbnail_path ?? null,
  songs: [
    ...data.songs.vega_songs.map((song) =>
      transformToSong(song, SongType.AI_GENERATED)
    ),
    ...data.songs.studio_project_songs.map((song) =>
      transformToSong(song, SongType.STUDIO)
    ),
    ...data.songs.user_songs.map((song) => transformToUserSong(song)),
  ],
});

export const transformToArtist = ({ id, name }: Artist) => ({
  id,
  name,
});

export const transformToExceptionCodeError = (
  response: ErrorResponse<ExceptionCodeErrorData>
): SerializedError => {
  const { data } = response;

  if (!data) {
    return { message: messages.error.unknown };
  }

  const { payload } = data;
  if (!payload) {
    return { message: response.data.error_description };
  }

  const { exception_code } = payload;
  if (exception_code) {
    return { code: exception_code, message: response.data.error_description };
  }

  return { message: messages.error.unknown };
};

export const transformToArtistNameError = (response: ErrorResponse) => {
  const { data } = response;
  if (!data) {
    return new Error(messages.error.unknown);
  }

  return new Error(response.data.error_description);
};

export const transformToError = (
  response: ErrorResponse<ReleaseValidationErrorData>
) => {
  const { data } = response;
  if (!data) {
    return { message: messages.error.unknown };
  }

  return { message: response.data.error_description };
};

export const transformToPaginatedList = ({
  items,
  pagination_data,
}: ReleaseListResponse) => ({
  releases: items.map(transformToRelease),
  areAllPagesLoaded:
    pagination_data.total_pages <= pagination_data.current_page,
});

export const transformToReleasesStatistics = ({
  DRAFT,
  PENDING,
  RELEASED,
  DECLINED,
}: ReleasesStatsResponse) => ({
  draft: DRAFT,
  pending: PENDING,
  released: RELEASED,
  declined: DECLINED,
});
